import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import {
  CaretUpFill,
  CaretDownFill,
  Pencil,
  Trash,
  Check,
  X
} from 'react-bootstrap-icons';
import { useProjects } from '../../contexts/ProjectContext';
import { Project } from '../../types/project.types';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import EmptyState from '../common/EmptyState';
import './ProjectTable.css';

interface ProjectTableProps {
  onSelectProject: (project: Project) => void;
  onEditProject: (project: Project) => void;
  onCreateProject?: () => void;
  showArchived?: boolean;
}

const ProjectTable: React.FC<ProjectTableProps> = ({
  onSelectProject,
  onEditProject,
  onCreateProject,
  showArchived = false
}) => {
  const { projects, selectProject, deleteProject, updateProject } = useProjects();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editedName, setEditedName] = useState<string>('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<number>(0);
  const perPage = 10;

  const allData = useMemo(
    () => projects.filter(p => p.isArchived === showArchived),
    [projects, showArchived]
  );

  const totalPages = Math.ceil(allData.length / perPage);
  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const data = useMemo(
    () => allData.slice(startIndex, endIndex),
    [allData, startIndex, endIndex]
  );

  const hasMore = currentPage < totalPages;

  // Reset to page 1 when showArchived changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [showArchived]);

  // Restore cursor position after render
  useEffect(() => {
    if (editInputRef.current && editingProjectId) {
      editInputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
    }
  }, [editedName, editingProjectId]);

  const handleProjectClick = (project: Project) => {
    selectProject(project);
    onSelectProject(project);
  };

  const handleDelete = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (projectToDelete) {
      try {
        await deleteProject(projectToDelete.id);
        setShowDeleteModal(false);
        setProjectToDelete(null);
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleEdit = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setEditingProjectId(project.id);
    setEditedName(project.name);
  };

  const handleSaveEdit = async (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    if (!editedName.trim()) {
      return;
    }
    try {
      await updateProject(project.id, { name: editedName.trim() });
      setEditingProjectId(null);
      setEditedName('');
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingProjectId(null);
    setEditedName('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const columns: ColumnDef<Project>[] = [
    {
      accessorKey: 'name',
      header: 'Project Name',
      cell: ({ row }) => {
        const isEditing = editingProjectId === row.original.id;

        if (isEditing) {
          return (
            <div className="project-name-cell" onClick={(e) => e.stopPropagation()}>
              <input
                ref={editInputRef}
                type="text"
                className="form-control form-control-sm project-edit-input"
                value={editedName}
                onChange={(e) => {
                  cursorPositionRef.current = e.target.selectionStart || 0;
                  setEditedName(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit(e as any, row.original);
                  } else if (e.key === 'Escape') {
                    handleCancelEdit(e as any);
                  }
                }}
                onClick={(e) => {
                  cursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                onKeyUp={(e) => {
                  cursorPositionRef.current = (e.target as HTMLInputElement).selectionStart || 0;
                }}
                autoFocus
              />
            </div>
          );
        }

        return (
          <div
            className="project-name-cell"
            onClick={() => handleProjectClick(row.original)}
          >
            <span className="project-name-text">
              {row.original.name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Date Created',
      cell: ({ getValue }) => (
        <span className="project-table-cell-text">
          {formatDate(getValue() as string)}
        </span>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Edited',
      cell: ({ getValue }) => (
        <span className="project-table-cell-text">
          {formatDate(getValue() as string)}
        </span>
      ),
    },
    {
      accessorKey: 'artifactCount',
      header: 'Artifact Count',
      cell: ({ getValue }) => (
        <span className="project-table-cell-text">
          {getValue() as number}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => {
        const isEditing = editingProjectId === row.original.id;

        if (isEditing) {
          return (
            <div className="d-flex gap-2 edit-container" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn btn-link p-0 edit-save table-action-btn"
                aria-label="Save changes"
                onClick={(e) => handleSaveEdit(e, row.original)}
              >
                <Check size={24} />
              </button>
              <button
                className="btn btn-link p-0 edit-cancel table-action-btn"
                aria-label="Cancel editing"
                onClick={handleCancelEdit}
              >
                <X size={24} />
              </button>
            </div>
          );
        }

        return (
          <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btn-link text-muted p-0 table-action-btn"
              aria-label="Edit project"
              onClick={(e) => handleEdit(e, row.original)}
            >
              <Pencil size={16} />
            </button>
            <button
              className="btn btn-link text-danger p-0 table-action-btn"
              aria-label="Delete project"
              onClick={(e) => handleDelete(e, row.original)}
            >
              <Trash size={16} />
            </button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  if (allData.length === 0) {
    return (
      <div className="d-flex justify-content-center py-4">
        <EmptyState
          type="projects"
          onButtonClick={onCreateProject || (() => {})}
        />
      </div>
    );
  }

  return (
    <>
      <div className="project-table-container">
        <table className="project-table">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={header.column.getCanSort() ? '' : 'no-sort'}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className="d-flex align-items-center"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <CaretUpFill className="ms-2" size={12} />,
                            desc: <CaretDownFill className="ms-2" size={12} />,
                          }[header.column.getIsSorted() as string] ?? (
                            header.column.getCanSort() ? (
                              <span className="ms-2 sort-indicator-inactive-opacity">
                                <CaretUpFill size={12} />
                              </span>
                            ) : null
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end mt-3 mb-3">
        <div className="pagination-controls d-flex align-items-center gap-2">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="pagination-number active">{currentPage}</span>
          {hasMore && (
            <span className="pagination-number">{currentPage + 1}</span>
          )}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={!hasMore}
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        itemType="Project"
        artifactCount={projectToDelete?.artifactCount || 0}
      />
    </>
  );
};

export default ProjectTable;